import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';

// Styled components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
`;

const Hero = styled.section`
  text-align: center;
  padding: 80px 0 60px;
  color: white;
  
  @media (max-width: 768px) {
    padding: 60px 0 40px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 20px;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  font-weight: 300;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Content = styled.div`
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 60px 40px;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.1);
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    border-radius: 20px 20px 0 0;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContactInfo = styled.div`
  h2 {
    font-size: 2rem;
    color: #2d3748;
    margin-bottom: 30px;
    font-weight: 600;
    position: relative;
    padding-bottom: 15px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #667eea, #764ba2);
      border-radius: 2px;
    }
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #4a5568;
    margin-bottom: 25px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 25px;
  padding: 20px;
  background: #f7fafc;
  border-radius: 15px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    border-color: #667eea;
  }
`;

const ContactIcon = styled.div`
  font-size: 2rem;
  margin-right: 20px;
  color: #667eea;
  min-width: 60px;
  text-align: center;
`;

const ContactDetails = styled.div`
  flex: 1;
  
  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 8px;
  }
  
  p {
    font-size: 1rem;
    color: #718096;
    margin: 0;
    line-height: 1.5;
  }
  
  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ContactForm = styled.div`
  h2 {
    font-size: 2rem;
    color: #2d3748;
    margin-bottom: 30px;
    font-weight: 600;
    position: relative;
    padding-bottom: 15px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #667eea, #764ba2);
      border-radius: 2px;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 10px;
`;

const Input = styled.input`
  padding: 15px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const TextArea = styled.textarea`
  padding: 15px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.3s ease;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  &::placeholder {
    color: #a0aec0;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 18px 40px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const FAQ = styled.div`
  margin-top: 60px;
  
  h2 {
    font-size: 2rem;
    color: #2d3748;
    margin-bottom: 30px;
    font-weight: 600;
    text-align: center;
    position: relative;
    padding-bottom: 15px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60px;
      height: 4px;
      background: linear-gradient(90deg, #667eea, #764ba2);
      border-radius: 2px;
    }
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const FAQItem = styled.div`
  background: #f7fafc;
  padding: 30px;
  border-radius: 15px;
  border: 1px solid #e2e8f0;
  
  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    
    &::before {
      content: '❓';
      margin-right: 10px;
      font-size: 1.5rem;
    }
  }
  
  p {
    font-size: 1rem;
    line-height: 1.6;
    color: #4a5568;
    margin: 0;
  }
`;

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      alert('Thank you for your message! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setIsSubmitting(false);
    }, 2000);
  };

  return (
    <PageContainer>
      <Helmet>
        <title>Contact Us - Infonium | Get in Touch</title>
        <meta name="description" content="Contact the Infonium team for support, feedback, or collaboration. We always listen and are ready to respond quickly." />
        <meta name="keywords" content="contact infonium, customer support, feedback, contact us, support, customer service" />
        <meta name="author" content="Infonium" />
        <meta property="og:title" content="Contact Us - Infonium | Get in Touch" />
        <meta property="og:description" content="Contact the Infonium team for support, feedback, or collaboration. We always listen and are ready to respond." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/contact'} />
        <meta property="og:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta property="og:site_name" content="Infonium" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us - Infonium | Get in Touch" />
        <meta name="twitter:description" content="Contact the Infonium team for support, feedback, or collaboration. We always listen and are ready to respond." />
        <meta name="twitter:image" content="https://cdn.infonium.org/img/logo_infonium.png" />
        <meta name="twitter:site" content="@infonium" />
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : 'https://www.infonium.org/contact'} />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="en" />
      </Helmet>
      
      <ContentWrapper>
        <Hero>
          <HeroTitle>Contact Us</HeroTitle>
          <HeroSubtitle>We'd love to hear from you. Get in touch with our team.</HeroSubtitle>
        </Hero>

        <Content>
          <ContactGrid>
            <ContactInfo>
              <h2>Get in Touch</h2>
              <p>
                Have questions, suggestions, or just want to say hello? We're here to help! 
                Reach out to us through any of the methods below, and we'll get back to you as soon as possible.
              </p>
              
              <ContactItem>
                <ContactIcon>📧</ContactIcon>
                <ContactDetails>
                  <h3>Email Us</h3>
                  <p>
                    <a href="mailto:contact@infonium.org">contact@infonium.org</a><br />
                    We typically respond within 24 hours
                  </p>
                </ContactDetails>
              </ContactItem>
              
              <ContactItem>
                <ContactIcon>🌐</ContactIcon>
                <ContactDetails>
                  <h3>Website</h3>
                  <p>
                    <a href="https://www.infonium.org" target="_blank" rel="noopener noreferrer">
                      www.infonium.org
                    </a><br />
                    Visit our main website for more information
                  </p>
                </ContactDetails>
              </ContactItem>
              
              <ContactItem>
                <ContactIcon>💬</ContactIcon>
                <ContactDetails>
                  <h3>Social Media</h3>
                  <p>
                    Follow us for updates and news<br />
                    <a href="https://twitter.com/infonium" target="_blank" rel="noopener noreferrer">@infonium</a>
                  </p>
                </ContactDetails>
              </ContactItem>
              
              <ContactItem>
                <ContactIcon>⏰</ContactIcon>
                <ContactDetails>
                  <h3>Response Time</h3>
                  <p>
                    We aim to respond to all inquiries<br />
                    within 24-48 hours
                  </p>
                </ContactDetails>
              </ContactItem>
            </ContactInfo>

            <ContactForm>
              <h2>Send us a Message</h2>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="What is this about?"
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="message">Message *</Label>
                  <TextArea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us more about your inquiry..."
                    required
                  />
                </FormGroup>
                
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </SubmitButton>
              </Form>
            </ContactForm>
          </ContactGrid>

          <FAQ>
            <h2>Frequently Asked Questions</h2>
            <FAQGrid>
              <FAQItem>
                <h3>How can I report a technical issue?</h3>
                <p>
                  Please send us an email with details about the issue you're experiencing, 
                  including your device type and browser. Screenshots are helpful too!
                </p>
              </FAQItem>
              
              <FAQItem>
                <h3>Can I suggest content or topics?</h3>
                <p>
                  Absolutely! We welcome content suggestions and topic ideas. 
                  Send us your suggestions and we'll consider them for future coverage.
                </p>
              </FAQItem>
              
              <FAQItem>
                <h3>Do you accept guest posts?</h3>
                <p>
                  We occasionally accept high-quality guest contributions. 
                  Please contact us with your proposal and writing samples.
                </p>
              </FAQItem>
              
              <FAQItem>
                <h3>How do I report inappropriate content?</h3>
                <p>
                  If you encounter content that violates our guidelines, 
                  please contact us immediately with the specific URL and details.
                </p>
              </FAQItem>
              
              <FAQItem>
                <h3>Can I advertise on Infonium?</h3>
                <p>
                  We offer various advertising opportunities. 
                  Contact us for information about our advertising packages and rates.
                </p>
              </FAQItem>
              
              <FAQItem>
                <h3>How do I unsubscribe from notifications?</h3>
                <p>
                  You can manage your notification preferences in your browser settings 
                  or contact us for assistance with unsubscribing.
                </p>
              </FAQItem>
            </FAQGrid>
          </FAQ>
        </Content>
      </ContentWrapper>
    </PageContainer>
  );
};

export default ContactPage; 